"use client";
import { handleButtonTracking } from "@/app/utils/button-tracking";
import { OpenModal } from "@/app/utils/open-email-modal";
import { EmailModal } from "@/components/EmailModal";
import { ExtensionCard } from "@/components/ExtensionCard";
import MailingList from "@/components/MailingList";
import { extensions } from "@/lib/extensions";
import { Download } from "lucide-react";
import { useState } from "react";

export default function ExtensionShowcase() {
  const [filter, setFilter] = useState("all");

  const filteredExtensions =
    filter === "all"
      ? extensions
      : extensions.filter((ext) => ext.filterTag === filter);

  const exportToCSV = () => {
    const headers = [
      "Name",
      "Category",
      "Description",
      "Revenue",
      "Revenue Model",
      "Users",
      "Rating",
      "Age",
      "Growth",
      // "Link",
    ];

    const csvData = filteredExtensions.map((ext) => [
      ext.name,
      ext.category,
      ext.description,
      ext.revenue,
      ext.revenueModel,
      ext.users,
      ext.rating,
      ext.age,
      ext.growth,
      // ext.link || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", `chrome-extensions-${filter}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="relative w-full max-w-7xl mx-auto pt-20 lg:pt-20 2xl:pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Successful Chrome Extensions
          </h1>
          <p className="text-xl lg:text-2xl text-zinc-400 max-w-3xl mx-auto mb-8">
            Real examples of extensions that turned into profitable businesses.
            Get inspired and build your own!
          </p>

          <div className="flex justify-center items-center gap-4 mb-6">
            <button
              onClick={() => {
                handleButtonTracking("clicked_download_directory_export");
                OpenModal("directory-export");
              }}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all shadow-md hover:shadow-lg"
            >
              <Download className="w-4 h-4" />
              Export to CSV
            </button>
          </div>

          <div className="flex justify-center gap-2 flex-wrap">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                filter === "all"
                  ? "bg-primary text-white shadow-lg ring-1 ring-inset ring-white/15"
                  : "bg-panel/60 border border-line text-zinc-300 hover:bg-panel-light"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter("productivity")}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                filter === "productivity"
                  ? "bg-primary text-white shadow-lg ring-1 ring-inset ring-white/15"
                  : "bg-panel/60 border border-line text-zinc-300 hover:bg-panel-light"
              }`}
            >
              Productivity
            </button>
            <button
              onClick={() => setFilter("shopping")}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                filter === "shopping"
                  ? "bg-primary text-white shadow-lg ring-1 ring-inset ring-white/15"
                  : "bg-panel/60 border border-line text-zinc-300 hover:bg-panel-light"
              }`}
            >
              Shopping
            </button>
            <button
              onClick={() => setFilter("security")}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                filter === "security"
                  ? "bg-primary text-white shadow-lg ring-1 ring-inset ring-white/15"
                  : "bg-panel/60 border border-line text-zinc-300 hover:bg-panel-light"
              }`}
            >
              Security
            </button>
            <button
              onClick={() => setFilter("privacy")}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                filter === "privacy"
                  ? "bg-primary text-white shadow-lg ring-1 ring-inset ring-white/15"
                  : "bg-panel/60 border border-line text-zinc-300 hover:bg-panel-light"
              }`}
            >
              Privacy
            </button>
            <button
              onClick={() => setFilter("tools")}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                filter === "tools"
                  ? "bg-primary text-white shadow-lg ring-1 ring-inset ring-white/15"
                  : "bg-panel/60 border border-line text-zinc-300 hover:bg-panel-light"
              }`}
            >
              Tools
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredExtensions.map((extension, index) => (
            <ExtensionCard key={index} extension={extension} />
          ))}
        </div>
        <MailingList />
        <EmailModal
          id="directory-export"
          title="Get your Export Now"
          desc="Enter your email below and we'll send you to the export. No spam, just value."
          cta="Download"
          tracking="user_entered_email_for_directory_export"
          onSuccess={exportToCSV}
        />
      </div>
    </div>
  );
}
